import React from 'react';
import { createStyles, CssBaseline, makeStyles, Theme } from '@material-ui/core';
import { AxiosError, AxiosResponse } from 'axios';
import clsx from 'clsx';

import { HomeContext } from '../../Home/HomeContext';
import { ErrorStatus } from '../../../shared/enum/ErrorStatus';
import { NewsModel, newsModelInitial } from '../../../Models/NewsModel';
import { NewsContext } from '../NewsContext';
import NewsService from '../../../Services/NewsService';
import NewsClassification from './NewsClassification';
import NewsList from './NewsList';
import { VehicleModel, vehicleModelInitial } from '../../../Models/VehicleModel';
import { ClassificationModel, classificationModelInitial } from '../../../Models/ClassificationModel';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { Revision } from '../Revision/views/Revision';
import { NewsLoadingContext } from '../NewsLoadingContext';
import { LoadingStatus } from '../enum/LoadingStatus';
import { DetachedClassification } from '../DetachedClassification/views/DetachedClassification';
import { DetachedClassificationAction } from '../enum/DetachedClassificationAction';
import { TableLoading } from '../../../shared/components/TableLoading';

const useStyles = makeStyles((theme: Theme) => createStyles({
    root: {
        display: 'flex',
    },
    content: {
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: '-528px',
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 1),
        ...theme.mixins.toolbar,
        justifyContent: 'flex-end',
    },
}));

export default function Classification() {

    const classes = useStyles();
    const homeContext = React.useContext(HomeContext);

    const [newsCurrentId, setNewsCurrentId] = React.useState<number | null>(null);
    const [news, setNews] = React.useState<NewsModel>(newsModelInitial);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [newsList, setNewsList] = React.useState<NewsModel[]>([]);
    const [newsIndex, setNewsIndex] = React.useState<number>(-1);
    const [vehicle, setVehicle] = React.useState<VehicleModel>(vehicleModelInitial);
    const [vehicleMetricCorrect, setVehicleMetricCorrect] = React.useState<boolean>(false);
    const [openList, setOpenList] = React.useState<boolean>(true);
    const [errorDataStatus, setErrorDataStatus] = React.useState<ErrorStatus>(ErrorStatus.NOT_INICIALIZED);
    const [errorCategorizationStatus, setErrorCategorizationStatus] = React.useState<ErrorStatus>(ErrorStatus.NOT_INICIALIZED);
    const [errorAtLeastOneCategorizationStatus, setErrorAtLeastOneCategorizationStatus] = React.useState<ErrorStatus>(ErrorStatus.NOT_INICIALIZED);
    const [listNewsPending, setListNewsPending] = React.useState<NewsModel[]>([]);
    const [keepData, setKeepData] = React.useState<boolean>(false);

    const [classification, setClassification] = React.useState<ClassificationModel>(classificationModelInitial);
    const [tabListSelected, setTabListSelected] = React.useState<number>(0);

    const [classificationLoading, setClassificationLoading] = React.useState<LoadingStatus>(LoadingStatus.NOT_STARTED);
    const [categorizationLoading, setCategorizationLoading] = React.useState<LoadingStatus>(LoadingStatus.NOT_STARTED);
    const [vehicleLoading, setVehicleLoading] = React.useState<LoadingStatus>(LoadingStatus.NOT_STARTED);

    const [detachedNewsDataAction, setDetachedNewsDataAction] = React.useState<DetachedClassificationAction>(DetachedClassificationAction.NONE);
    const [detachedNewsCategorizationAction, setDetachedNewsCategorizationAction] = React.useState<DetachedClassificationAction>(DetachedClassificationAction.NONE);

    React.useEffect(() => {
        if (homeContext.spreadsheetId) {
            NewsService.newsService(homeContext.spreadsheetId)
                .getAll()
                .then((res: AxiosResponse) => { setNewsList(res.data); })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
        }
    }, [homeContext.spreadsheetId]);

    React.useEffect(() => {
        if (classificationLoading == LoadingStatus.LOADING
            || vehicleLoading == LoadingStatus.LOADING
            || categorizationLoading == LoadingStatus.LOADING) {

            homeContext.setOpenLoadingDialog(true);
        }

        if (classificationLoading == LoadingStatus.DONE
            && vehicleLoading == LoadingStatus.DONE
            && categorizationLoading == LoadingStatus.DONE) {

            setClassificationLoading(LoadingStatus.NOT_STARTED);
            setCategorizationLoading(LoadingStatus.NOT_STARTED);
            setVehicleLoading(LoadingStatus.NOT_STARTED);

            homeContext.setOpenLoadingDialog(false);
        }

        if (classificationLoading == LoadingStatus.ERROR
            || vehicleLoading == LoadingStatus.ERROR
            || categorizationLoading == LoadingStatus.ERROR) {

            setClassificationLoading(LoadingStatus.NOT_STARTED);
            setCategorizationLoading(LoadingStatus.NOT_STARTED);
            setVehicleLoading(LoadingStatus.NOT_STARTED);

            homeContext.setOpenLoadingDialog(false);
        }

    }, [classificationLoading, categorizationLoading, vehicleLoading]);

    return (
        <>
            <div className={classes.root}>
                <NewsContext.Provider value={{
                    news, setNews,
                    newsList, setNewsList,
                    newsIndex, setNewsIndex,
                    newsCurrentId, setNewsCurrentId,
                    vehicle, setVehicle,
                    vehicleMetricCorrect, setVehicleMetricCorrect,
                    errorDataStatus, setErrorDataStatus,
                    errorCategorizationStatus, setErrorCategorizationStatus,
                    errorAtLeastOneCategorizationStatus, setErrorAtLeastOneCategorizationStatus,
                    listNewsPending, setListNewsPending,
                    keepData, setKeepData,
                    classification, setClassification,
                    tabListSelected, setTabListSelected,
                    detachedNewsDataAction, setDetachedNewsDataAction,
                    detachedNewsCategorizationAction, setDetachedNewsCategorizationAction
                }}>
                    <NewsLoadingContext.Provider value={{
                        classificationLoading, setClassificationLoading,
                        categorizationLoading, setCategorizationLoading,
                        vehicleLoading, setVehicleLoading,
                    }}>
                        {homeContext.pageLocation === PageLocation.CLASSIFICATION && (
                            <>
                                {!loading && (<>
                                    <CssBaseline />
                                    <NewsList open={openList} setOpen={setOpenList} spreadsheetId={homeContext.spreadsheetId} setLoading={setLoading} />
                                    <main className={clsx(classes.content, { [classes.contentShift]: openList })}>
                                        {homeContext.spreadsheetId &&
                                            <NewsClassification
                                                spreadsheetId={homeContext.spreadsheetId ?? 0}
                                                openList={openList}
                                                setOpenList={setOpenList} />
                                        }
                                    </main></>)}
                                {loading &&
                                    <TableLoading displayText="Carregando Lista de Noticias" />}
                            </>
                        )}

                        {(homeContext.pageLocation === PageLocation.REPORT_REVISION ||
                            homeContext.pageLocation === PageLocation.BATCH_CORRECTION) && (
                                <Revision />
                            )}
                        {(homeContext.pageLocation === PageLocation.DETACHED_CLASSIFICATION_INITIAL ||
                            homeContext.pageLocation === PageLocation.DETACHED_CLASSIFICATION) &&
                            <DetachedClassification />
                        }
                    </NewsLoadingContext.Provider>
                </NewsContext.Provider>
            </div>
        </>
    );
}